import React, { useState, useEffect, useCallback } from 'react';
import { settlementSchedulesApi, unitsApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Textarea } from './ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import {
  Send, CalendarClock, Plus, Trash2, Pencil, Play, Check, Truck, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { apiErrorMessage } from '../lib/utils';

const FREQ = [
  { id: 'daily', label: 'Daily' },
  { id: 'weekly', label: 'Weekly' },
  { id: 'biweekly', label: 'Every 2 weeks' },
  { id: 'monthly', label: 'Monthly' },
];
const DOWS = [
  { id: 'mon', label: 'Mon' }, { id: 'tue', label: 'Tue' }, { id: 'wed', label: 'Wed' },
  { id: 'thu', label: 'Thu' }, { id: 'fri', label: 'Fri' }, { id: 'sat', label: 'Sat' },
  { id: 'sun', label: 'Sun' },
];

const empty = () => ({
  id: null,
  name: '',
  frequency: 'weekly',
  day_of_week: 'fri',
  day_of_month: 15,
  hour: 15,
  minute: 0,
  unit_ids: [],
  all_units: false,
  enabled: true,
  message: '',
  recipient_overrides: {},   // unit_id -> custom email
});

const summarize = (s, unitMap) => {
  const time = `${String(s.hour).padStart(2, '0')}:${String(s.minute).padStart(2, '0')}`;
  let when = '';
  if (s.frequency === 'daily') when = `Every day at ${time}`;
  else if (s.frequency === 'weekly') when = `Every ${(s.day_of_week || '?').toUpperCase()} at ${time}`;
  else if (s.frequency === 'biweekly') when = `Every 2 wks · ${(s.day_of_week || '?').toUpperCase()} @ ${time}`;
  else if (s.frequency === 'monthly') when = `Monthly · day ${s.day_of_month} at ${time}`;

  const targets = s.all_units
    ? 'All active units'
    : `${(s.unit_ids || []).length} unit${(s.unit_ids || []).length === 1 ? '' : 's'}`;
  return `${when} · ${targets}`;
};

/**
 * SettlementScheduleCard — compact CRUD for automated settlement email
 * schedules.  Meant to sit alongside existing settlement UI without covering
 * anything else.  Mobile-friendly (form scrolls vertically inside the dialog).
 */
export default function SettlementScheduleCard() {
  const [schedules, setSchedules] = useState([]);
  const [units, setUnits] = useState([]);
  const [recipients, setRecipients] = useState([]);  // unit_id -> { recipient_name, recipient_email, ... }
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(empty());
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, u, r] = await Promise.all([
        settlementSchedulesApi.list(),
        unitsApi.getAll(),
        settlementSchedulesApi.unitRecipients(),
      ]);
      setSchedules(s.data.schedules || []);
      setUnits(Array.isArray(u.data) ? u.data : (u.data.units || []));
      setRecipients(r.data.units || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const unitMap = Object.fromEntries(units.map((u) => [u.id, u.unit_number]));
  const recipientMap = Object.fromEntries(recipients.map((r) => [r.unit_id, r]));

  const openCreate = () => { setForm(empty()); setDialogOpen(true); };
  const openEdit = (s) => {
    setForm({
      id: s.id,
      name: s.name || '',
      frequency: s.frequency || 'weekly',
      day_of_week: s.day_of_week || 'fri',
      day_of_month: s.day_of_month || 15,
      hour: s.hour ?? 15,
      minute: s.minute ?? 0,
      unit_ids: s.unit_ids || [],
      all_units: !!s.all_units,
      enabled: s.enabled !== false,
      message: s.message || '',
      recipient_overrides: s.recipient_overrides || {},
    });
    setDialogOpen(true);
  };

  const toggleUnit = (id) => {
    setForm((prev) => {
      const has = prev.unit_ids.includes(id);
      return { ...prev, unit_ids: has ? prev.unit_ids.filter((x) => x !== id) : [...prev.unit_ids, id] };
    });
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error('Give the schedule a name');
    if (!form.all_units && form.unit_ids.length === 0) {
      return toast.error('Pick at least one unit — or check "All units"');
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        frequency: form.frequency,
        day_of_week: (form.frequency === 'weekly' || form.frequency === 'biweekly') ? form.day_of_week : null,
        day_of_month: form.frequency === 'monthly' ? Number(form.day_of_month) : null,
        hour: Number(form.hour),
        minute: Number(form.minute),
        unit_ids: form.all_units ? [] : form.unit_ids,
        all_units: form.all_units,
        enabled: form.enabled,
        message: form.message || null,
        // Only persist overrides for units that are actually targeted +
        // whose value is non-empty. Prevents stale overrides for removed units.
        recipient_overrides: Object.fromEntries(
          Object.entries(form.recipient_overrides || {})
            .filter(([uid, email]) => (form.all_units || form.unit_ids.includes(uid)) && (email || '').trim())
            .map(([uid, email]) => [uid, email.trim()])
        ),
      };
      if (form.id) {
        await settlementSchedulesApi.patch(form.id, payload);
        toast.success('Schedule updated');
      } else {
        await settlementSchedulesApi.create(payload);
        toast.success('Schedule created');
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Save failed'));
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this schedule?')) return;
    try {
      await settlementSchedulesApi.delete(id);
      toast.success('Deleted');
      load();
    } catch (e) { toast.error(apiErrorMessage(e, 'Delete failed')); }
  };

  const runNow = async (s) => {
    if (!window.confirm(`Run "${s.name}" right now and email settlements to ${s.all_units ? 'all active units' : `${(s.unit_ids || []).length} unit(s)`}?`)) return;
    try {
      const r = await settlementSchedulesApi.runNow(s.id);
      const { sent, failed } = r.data || {};
      if (sent > 0 && failed === 0) {
        toast.success(`Sent ${sent} settlement${sent === 1 ? '' : 's'}`);
      } else if (sent > 0 && failed > 0) {
        toast.warning(`Sent ${sent} · Failed ${failed} — check schedule details`);
      } else if (sent === 0 && failed > 0) {
        // Peek at the first failure to give a useful hint
        const firstErr = (r.data?.per_unit || []).find((u) => !u.ok)?.error;
        toast.error(`Nothing sent — ${firstErr || 'check driver emails on target units'}`);
      } else {
        toast.info('Nothing to send for this schedule.');
      }
      load();
    } catch (e) { toast.error(apiErrorMessage(e, 'Run failed')); }
  };

  const toggleEnabled = async (s) => {
    try {
      await settlementSchedulesApi.patch(s.id, { enabled: !s.enabled });
      load();
    } catch (e) { toast.error(apiErrorMessage(e, 'Update failed')); }
  };

  return (
    <>
      <Card data-testid="settlement-schedules-card">
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-base font-manrope flex items-center gap-2">
            <CalendarClock className="h-4 w-4 text-blue-600" />
            Scheduled Settlements
            <Badge variant="outline" className="text-[10px]">{schedules.length}</Badge>
          </CardTitle>
          <Button size="sm" onClick={openCreate} className="bg-blue-600 hover:bg-blue-700" data-testid="schedule-create-btn">
            <Plus className="h-3.5 w-3.5 mr-1" /> New Schedule
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          {loading && (
            <div className="p-4 text-xs text-muted-foreground animate-pulse">Loading schedules…</div>
          )}
          {!loading && schedules.length === 0 && (
            <div className="p-6 text-center text-sm text-muted-foreground">
              <CalendarClock className="h-6 w-6 mx-auto mb-2 opacity-40" />
              No schedules yet. Create one to auto-email settlement PDFs to drivers.
            </div>
          )}
          <div className="divide-y">
            {schedules.map((s) => (
              <div key={s.id}
                   className="px-4 py-3 hover:bg-muted/30 transition-colors flex items-center gap-3"
                   data-testid={`schedule-row-${s.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm truncate max-w-[220px]">{s.name}</span>
                    <Badge variant={s.enabled ? 'default' : 'outline'}
                           className={`text-[10px] ${s.enabled ? 'bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-100' : ''}`}>
                      {s.enabled ? 'Active' : 'Paused'}
                    </Badge>
                  </div>
                  <div className="text-xs text-muted-foreground">{summarize(s, unitMap)}</div>
                  {s.last_run_at && (
                    <div className="text-[10px] text-muted-foreground mt-0.5">
                      Last run: {new Date(s.last_run_at).toLocaleString()} ·{' '}
                      {s.last_result?.sent ?? 0} sent · {s.last_result?.failed ?? 0} failed
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Switch checked={!!s.enabled} onCheckedChange={() => toggleEnabled(s)}
                          data-testid={`schedule-toggle-${s.id}`} />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => runNow(s)}
                    className="h-7 gap-1 border-blue-200 text-blue-700 hover:bg-blue-50 hover:text-blue-800"
                    title="Send this schedule right now"
                    data-testid={`schedule-run-${s.id}`}
                  >
                    <Play className="h-3 w-3" />
                    <span className="text-[11px] font-medium">Run now</span>
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => openEdit(s)}
                          title="Edit" data-testid={`schedule-edit-${s.id}`}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => remove(s.id)}
                          title="Delete" data-testid={`schedule-delete-${s.id}`}>
                    <Trash2 className="h-3.5 w-3.5 text-red-600" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[620px] w-[94vw] max-h-[85vh] p-0 flex flex-col"
                       data-testid="schedule-dialog">
          <DialogHeader className="p-4 pb-2 border-b">
            <DialogTitle className="flex items-center gap-2">
              <CalendarClock className="h-4 w-4 text-blue-600" />
              {form.id ? 'Edit Schedule' : 'New Settlement Schedule'}
            </DialogTitle>
            <DialogDescription className="text-xs">
              Automate settlement email delivery. Emails go to each unit&apos;s assigned driver.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-4">
            <div>
              <Label className="text-xs mb-1 block">Name</Label>
              <Input value={form.name} onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))}
                     placeholder="Friday Weekly Settlements" data-testid="schedule-name-input" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs mb-1 block">Frequency</Label>
                <Select value={form.frequency} onValueChange={(v) => setForm((s) => ({ ...s, frequency: v }))}>
                  <SelectTrigger className="h-9" data-testid="schedule-frequency">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FREQ.map((f) => <SelectItem key={f.id} value={f.id}>{f.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {(form.frequency === 'weekly' || form.frequency === 'biweekly') && (
                <div>
                  <Label className="text-xs mb-1 block">Day of week</Label>
                  <div className="flex gap-1 flex-wrap">
                    {DOWS.map((d) => (
                      <button
                        type="button"
                        key={d.id}
                        onClick={() => setForm((s) => ({ ...s, day_of_week: d.id }))}
                        className={`px-2 py-1 rounded text-xs border transition-colors ${
                          form.day_of_week === d.id
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'bg-white text-slate-700 border-slate-300 hover:border-blue-400'
                        }`}
                        data-testid={`schedule-dow-${d.id}`}
                      >
                        {d.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {form.frequency === 'monthly' && (
                <div>
                  <Label className="text-xs mb-1 block">Day of month (1-28)</Label>
                  <Input type="number" min={1} max={28} value={form.day_of_month}
                         onChange={(e) => setForm((s) => ({ ...s, day_of_month: e.target.value }))}
                         data-testid="schedule-dom-input" />
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs mb-1 block">Hour (0-23) UTC</Label>
                <Input type="number" min={0} max={23} value={form.hour}
                       onChange={(e) => setForm((s) => ({ ...s, hour: e.target.value }))}
                       data-testid="schedule-hour-input" />
              </div>
              <div>
                <Label className="text-xs mb-1 block">Minute</Label>
                <Input type="number" min={0} max={59} value={form.minute}
                       onChange={(e) => setForm((s) => ({ ...s, minute: e.target.value }))}
                       data-testid="schedule-minute-input" />
              </div>
            </div>

            <div className="rounded-lg border border-blue-200 bg-blue-50/40 p-3 space-y-2">
              <div className="flex items-center gap-2">
                <Switch checked={form.all_units}
                        onCheckedChange={(v) => setForm((s) => ({ ...s, all_units: !!v, unit_ids: v ? [] : s.unit_ids }))}
                        data-testid="schedule-all-units" />
                <Label className="text-xs">Send for <b>all active units</b> (auto-includes future units)</Label>
              </div>
              {!form.all_units && (
                <div>
                  <Label className="text-xs text-muted-foreground">Or pick specific units — driver&apos;s email shown below each name. Click ✎ to send to a different address.</Label>
                  <div className="mt-1 space-y-1 max-h-[260px] overflow-y-auto pr-1">
                    {units.map((u) => {
                      const checked = form.unit_ids.includes(u.id);
                      const rcp = recipientMap[u.id] || {};
                      const override = form.recipient_overrides?.[u.id] || '';
                      const effectiveEmail = override || rcp.recipient_email || '';
                      const noRecipient = !effectiveEmail;
                      const isEditing = form._editingOverrideFor === u.id;
                      return (
                        <div
                          key={u.id}
                          className={`rounded-md border p-2 text-xs transition-colors ${
                            checked
                              ? 'bg-white border-blue-400'
                              : 'bg-white/60 border-slate-200 hover:border-blue-300'
                          }`}
                          data-testid={`schedule-unit-checkbox-${u.id}`}
                        >
                          <label className="flex items-start gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              className="mt-0.5 accent-blue-600"
                              checked={checked}
                              onChange={() => toggleUnit(u.id)}
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5">
                                <Truck className="h-3 w-3 text-muted-foreground shrink-0" />
                                <span className="font-medium text-slate-800 truncate">{u.unit_number}</span>
                                {(rcp.recipient_role === 'owner_operator' || rcp.recipient_role === 'driver') && (
                                  <span className={`text-[9px] px-1 py-0.5 rounded ${
                                    rcp.recipient_role === 'driver'
                                      ? 'bg-pink-100 text-pink-700'
                                      : 'bg-orange-100 text-orange-700'
                                  }`}>
                                    {rcp.recipient_role === 'driver' ? 'Driver' : 'Owner-op'}
                                  </span>
                                )}
                              </div>
                              <div className="text-[10px] text-slate-500 truncate">
                                {rcp.recipient_name || <span className="italic text-slate-400">Unassigned</span>}
                              </div>
                              <div className={`text-[10px] truncate ${noRecipient ? 'text-red-600' : (override ? 'text-blue-700' : 'text-slate-500')}`}>
                                {noRecipient ? '⚠ no email on file — set an override below' : effectiveEmail}
                                {override && <span className="ml-1 text-[9px] uppercase tracking-wide">(override)</span>}
                              </div>
                            </div>
                          </label>
                          {checked && (
                            <div className="mt-1.5 pl-6 flex items-center gap-1">
                              {!isEditing ? (
                                <button
                                  type="button"
                                  className="text-[10px] text-blue-600 hover:underline flex items-center gap-0.5"
                                  onClick={() => setForm((s) => ({ ...s, _editingOverrideFor: u.id }))}
                                  data-testid={`schedule-override-edit-${u.id}`}
                                >
                                  <Pencil className="h-2.5 w-2.5" /> {override ? 'Change override email' : 'Send to a different email'}
                                </button>
                              ) : (
                                <>
                                  <Input
                                    type="email"
                                    placeholder="accounting@fleet.com"
                                    defaultValue={override}
                                    className="h-6 text-[10px] flex-1"
                                    data-testid={`schedule-override-input-${u.id}`}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        e.currentTarget.blur();
                                      }
                                    }}
                                    onBlur={(e) => {
                                      const val = e.target.value.trim();
                                      setForm((s) => {
                                        const next = { ...(s.recipient_overrides || {}) };
                                        if (val) next[u.id] = val;
                                        else delete next[u.id];
                                        return { ...s, recipient_overrides: next, _editingOverrideFor: null };
                                      });
                                    }}
                                    autoFocus
                                  />
                                  {override && (
                                    <button
                                      type="button"
                                      className="text-[10px] text-red-600 hover:underline"
                                      onClick={() => {
                                        setForm((s) => {
                                          const next = { ...(s.recipient_overrides || {}) };
                                          delete next[u.id];
                                          return { ...s, recipient_overrides: next, _editingOverrideFor: null };
                                        });
                                      }}
                                      data-testid={`schedule-override-clear-${u.id}`}
                                    >
                                      clear
                                    </button>
                                  )}
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <div>
              <Label className="text-xs mb-1 block">Message (optional)</Label>
              <Textarea rows={2} value={form.message}
                        onChange={(e) => setForm((s) => ({ ...s, message: e.target.value }))}
                        placeholder="Your weekly settlement is attached — reach out if anything looks off."
                        data-testid="schedule-message" />
            </div>

            <div className="flex items-center gap-2">
              <Switch checked={form.enabled}
                      onCheckedChange={(v) => setForm((s) => ({ ...s, enabled: !!v }))}
                      data-testid="schedule-enabled" />
              <Label className="text-xs">Enabled</Label>
            </div>
          </div>

          <DialogFooter className="p-3 border-t gap-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="schedule-cancel">Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="schedule-save">
              {saving ? 'Saving…' : (form.id ? 'Save Changes' : 'Create Schedule')}
              <ChevronRight className="h-3.5 w-3.5 ml-1" />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
